export const installWidget = async (
  baseUrl: string,
  domain: string
): Promise<{ installationId: string }> => {
  const response = await fetch(`${baseUrl}/api/widget/install`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ domain }),
  });
  if (!response.ok) {
    throw new Error("Failed to install widget");
  }
  return response.json();
};

export const getInstallationStatus = async (
  baseUrl: string,
  domain: string
): Promise<{ conversationMinutesUsed: number; conversationMinutesQuota: number; remainingMinutes: number }> => {
  const url = new URL(`${baseUrl}/api/widget/usage`);
  url.searchParams.append("domain", domain);
  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error("Failed to get installation status");
  }
  return response.json();
};

export const checkQuotaStatus = async (
  baseUrl: string,
  domain: string
): Promise<{ hasQuota: boolean; remainingMinutes: number }> => {
  try {
    const status = await getInstallationStatus(baseUrl, domain);
    return {
      hasQuota: status.remainingMinutes > 0,
      remainingMinutes: status.remainingMinutes
    };
  } catch (error) {
    console.error('[RemstalVoice] Failed to check quota status:', error);
    // If we can't check quota, assume we have quota to avoid blocking
    return { hasQuota: true, remainingMinutes: 0 };
  }
};

export const endConversationSession = async (
  baseUrl: string,
  sessionId: string
): Promise<void> => {
  console.log(`[RemstalVoice] Ending conversation session: ${sessionId}`);
  const response = await fetch(`${baseUrl}/api/widget/end-session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ session_id: sessionId }),
  });
  
  if (!response.ok) {
    console.error(`[RemstalVoice] endConversationSession API failed:`, response.status, response.statusText);
    throw new Error(`Failed to end conversation session: ${response.status}`);
  }
  
  console.log(`[RemstalVoice] Session ended successfully: ${sessionId}`);
};

export const endConversationSessionOnUnload = (
  baseUrl: string,
  sessionId: string
): void => {
  const url = `${baseUrl}/api/widget/end-session`;
  const body = JSON.stringify({ session_id: sessionId });
  try {
    if (typeof navigator.sendBeacon === "function") {
      const queued = navigator.sendBeacon(
        url,
        new Blob([body], { type: "application/json" })
      );
      if (queued) return;
    }
  } catch (error) {
    console.error("[RemstalVoice] Beacon session cleanup failed:", error);
  }

  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).then((response) => {
    if (!response.ok) {
      console.error(
        "[RemstalVoice] Keepalive session cleanup failed:",
        response.status,
        response.statusText
      );
    }
  }).catch((error: unknown) => {
    console.error("[RemstalVoice] Keepalive session cleanup failed:", error);
  });
};

export const updateMessageCount = async (
  baseUrl: string,
  sessionId: string
): Promise<void> => {
  console.log(`[RemstalVoice] Calling updateMessageCount API for session: ${sessionId}`);
  const response = await fetch(`${baseUrl}/api/widget/update-message-count`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ session_id: sessionId }),
  });
  
  if (!response.ok) {
    console.error(`[RemstalVoice] updateMessageCount API failed:`, response.status, response.statusText);
    throw new Error(`Failed to update message count: ${response.status}`);
  }
  
  console.log(`[RemstalVoice] Message count updated successfully for session: ${sessionId}`);
};

export const sendHeartbeat = async (
  baseUrl: string,
  sessionId: string
): Promise<void> => {
  console.log(`[RemstalVoice] Sending heartbeat for session: ${sessionId}`);
  const response = await fetch(`${baseUrl}/api/widget/heartbeat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ session_id: sessionId }),
  });
  
  if (!response.ok) {
    console.error(`[RemstalVoice] Heartbeat API failed:`, response.status, response.statusText);
    throw new Error(`Failed to send heartbeat: ${response.status}`);
  }
  
  console.log(`[RemstalVoice] Heartbeat sent successfully for session: ${sessionId}`);
};
